import { movies, type Movie, type MovieType } from '@/mocks/movies';

export const TYPE_LABELS: Record<MovieType, string> = {
  movie: '电影',
  tv: '电视剧',
  variety: '综艺',
};

export interface CategoryMeta {
  type: MovieType;
  label: string;
  channel: string;
  description: string;
  seoTitle: string;
  seoDescription: string;
}

export const CATEGORY_META: Record<MovieType, CategoryMeta> = {
  movie: {
    type: 'movie',
    label: '电影',
    channel: '电影频道',
    description: '汇聚院线佳作与口碑经典，涵盖科幻、悬疑、动作、爱情等多元类型，随时找到你想看的那一部。',
    seoTitle: '电影 - 丝瓜视频影视分类',
    seoDescription: '丝瓜视频电影频道聚合热门院线电影与经典佳作，支持按地区、年份、热度筛选，快速发现优质电影内容。',
  },
  tv: {
    type: 'tv',
    label: '电视剧',
    channel: '电视剧频道',
    description: '从都市情感、悬疑谍战到历史史诗，追更热播剧集，不错过每一集精彩。',
    seoTitle: '电视剧 - 丝瓜视频影视分类',
    seoDescription: '丝瓜视频电视剧频道提供都市、悬疑、历史等热播剧集，支持地区年份筛选与热度排序，轻松追剧。',
  },
  variety: {
    type: 'variety',
    label: '综艺',
    channel: '综艺频道',
    description: '真人秀、音乐竞演、美食纪实与户外冒险，轻松解压，快乐加倍。',
    seoTitle: '综艺 - 丝瓜视频影视分类',
    seoDescription: '丝瓜视频综艺频道聚合真人秀、音乐、美食、户外等热门综艺节目，随时挑选好看又轻松的节目。',
  },
};

export const REGIONS = ['全部', '内地', '美国', '英国', '日本', '韩国'];
export const YEARS = ['全部', '2025', '2024', '2023'];
export const SORTS = [
  { value: 'latest', label: '最新' },
  { value: 'hot', label: '热门' },
  { value: 'rating', label: '评分' },
] as const;

export type SortValue = (typeof SORTS)[number]['value'];

export function getMoviesByType(type: MovieType): Movie[] {
  return movies.filter((m) => m.type === type);
}

export function getMovieById(id: string): Movie | undefined {
  return movies.find((m) => m.id === id);
}

export function getFeaturedMovies(): Movie[] {
  return movies.filter((m) => m.featured);
}

export function getLatestMovies(limit = 10): Movie[] {
  return [...movies].sort((a, b) => (a.updateTime < b.updateTime ? 1 : -1)).slice(0, limit);
}

export function getHotMovies(limit = 8): Movie[] {
  return [...movies].sort((a, b) => b.views - a.views).slice(0, limit);
}

export interface FilterOptions {
  type?: MovieType;
  region?: string;
  year?: string;
  sort?: SortValue;
}

export function filterMovies(options: FilterOptions): Movie[] {
  const { type, region, year, sort } = options;
  let list = [...movies];
  if (type) list = list.filter((m) => m.type === type);
  if (region && region !== '全部') list = list.filter((m) => m.region === region);
  if (year && year !== '全部') list = list.filter((m) => String(m.year) === year);

  if (sort === 'hot') list.sort((a, b) => b.views - a.views);
  else if (sort === 'rating') list.sort((a, b) => b.rating - a.rating);
  else list.sort((a, b) => (a.updateTime < b.updateTime ? 1 : -1));

  return list;
}

export function searchMovies(keyword: string): Movie[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return movies.filter((m) => {
    const haystack = [m.title, m.director, m.region, ...m.genres, ...m.cast].join(' ').toLowerCase();
    return haystack.includes(kw);
  });
}

export function getRelatedMovies(movie: Movie, limit = 5): Movie[] {
  return movies
    .filter((m) => m.id !== movie.id && m.type === movie.type)
    .sort((a, b) => b.views - a.views)
    .slice(0, limit);
}

export function paginate<T>(list: T[], page: number, pageSize: number): { items: T[]; totalPages: number } {
  const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return { items: list.slice(start, start + pageSize), totalPages };
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}亿`;
  return `${views}万`;
}